import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../utils/apiClient";
import { Order } from "../../types/Interface";
import { useVendorContext } from "../../context/VendorContext";

type SortKey = "id" | "createdAt" | "status" | "paymentStatus";
type SortOrder = "asc" | "desc";

const Orders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const navigate = useNavigate();
  const { vendor, role } = useVendorContext();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiRequest("/orders");

        let filteredOrders = response;

        // Filter if role is VENDOR
        // if (role === "VENDOR" && vendor?.vendorName) {
          filteredOrders = response.filter((order: Order) =>
            order.orderItems?.some(
              (item) => item.product?.vendor === vendor.vendorName
            )
          );
        // }

        setOrders(filteredOrders);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [role, vendor]);

  const handleSort = (key: SortKey) => {
    const newOrder = key === sortKey && sortOrder === "asc" ? "desc" : "asc";
    setSortKey(key);
    setSortOrder(newOrder);

    const sorted = [...orders].sort((a, b) => {
      const aValue = a[key] ?? "";
      const bValue = b[key] ?? "";
      if (aValue < bValue) return newOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return newOrder === "asc" ? 1 : -1;
      return 0;
    });

    setOrders(sorted);
  };

  const goToOrderDetails = (orderId: number) => {
    navigate(`/orders/${orderId}`);
  };

  const formatDate = (value?: string) => {
    if (!value) return "N/A";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadgeClass = (status?: string) => {
    const value = (status || "").toUpperCase();
    if (["DELIVERED", "COMPLETE"].includes(value)) return "bg-emerald-100 text-emerald-800";
    if (["CANCELLED", "FAILED"].includes(value)) return "bg-rose-100 text-rose-800";
    if (["SHIPPED", "OUT_FOR_DELIVERY"].includes(value)) return "bg-orange-100 text-orange-800";
    if (["PROCESSING", "CONFIRMED"].includes(value)) return "bg-blue-100 text-blue-800";
    return "bg-slate-100 text-slate-700";
  };

  return (
    <div className="max-w-9xl mx-auto p-4 md:p-6">
      <div className="mb-5 rounded-xl border border-slate-200 bg-gradient-to-r from-amber-50 via-white to-slate-50 p-4 md:p-5 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">Website Orders</h1>
        <p className="mt-1 text-sm text-slate-600">
          View and manage order status quickly with compact order rows.
        </p>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-slate-500">Total Orders</p>
          <p className="text-lg font-semibold text-slate-900">{orders.length}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-slate-500">Role</p>
          <p className="text-lg font-semibold text-slate-900">{role || "N/A"}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-slate-500">Vendor</p>
          <p className="text-lg font-semibold text-slate-900">{vendor?.vendorName || "N/A"}</p>
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-slate-600 shadow-sm">Loading orders...</div>
      ) : orders.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-slate-500 shadow-sm">No orders found.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full table-auto text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 border-b border-slate-200 text-left cursor-pointer" onClick={() => handleSort("id")}>Order ID</th>
                <th className="px-3 py-2 border-b border-slate-200 text-left cursor-pointer" onClick={() => handleSort("createdAt")}>Date</th>
                <th className="px-3 py-2 border-b border-slate-200 text-left cursor-pointer" onClick={() => handleSort("status")}>Status</th>
                <th className="px-3 py-2 border-b border-slate-200 text-left">Customer</th>
                <th className="px-3 py-2 border-b border-slate-200 text-left">Mobile</th>
                <th className="px-3 py-2 border-b border-slate-200 text-left">Product</th>
                <th className="px-3 py-2 border-b border-slate-200 text-left">Qty</th>
                <th className="px-3 py-2 border-b border-slate-200 text-left">Price</th>
                <th className="px-3 py-2 border-b border-slate-200 text-left cursor-pointer" onClick={() => handleSort("paymentStatus")}>Payment</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) =>
                order.orderItems.length > 0 ? (
                  order.orderItems.map((item, index) => (
                    <tr key={`${order.id}-${index}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td
                        className="px-3 py-2 text-blue-700 underline cursor-pointer font-medium"
                        onClick={() => goToOrderDetails(order.id)}
                      >
                        #{order.id}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{formatDate(order.createdAt)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusBadgeClass(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{order.user?.email ?? "N/A"}</td>
                      <td className="px-3 py-2 text-slate-700">{order.user?.mobile ?? "N/A"}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {item.product?.productName ?? "N/A"} ({item.product?.productCode ?? "N/A"})
                      </td>
                      <td className="px-3 py-2 text-slate-700">{item.quantity}</td>
                      <td className="px-3 py-2 font-medium text-slate-800">₹{Number(order.orderTotal || 0).toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusBadgeClass(order.paymentStatus)}`}>
                          {order.paymentStatus ?? "N/A"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr key={order.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td
                      className="px-3 py-2 text-blue-700 underline cursor-pointer font-medium"
                      onClick={() => goToOrderDetails(order.id)}
                    >
                      #{order.id}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{formatDate(order.createdAt)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusBadgeClass(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{order.user?.email ?? "N/A"}</td>
                    <td className="px-3 py-2 text-slate-700">{order.user?.mobile ?? "N/A"}</td>
                    <td className="px-3 py-2 text-slate-500" colSpan={3}>
                      No products in this order
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusBadgeClass(order.paymentStatus)}`}>
                        {order.paymentStatus ?? "N/A"}
                      </span>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Orders;
